/* Darstellung einer Entity nach ihrer Klasse (aus der FGD): welches Sprite sie zeigt. Ohne DOM. */
import EntDefs from './entdefs.ts';
import type { Entity } from './types.ts';

/** Eingebaute Klassen, deren Feld model ein Sprite ist (wie vor der FGD-Unterstützung) */
const SPRITE_MODEL = /^(env_sprite|env_glow|cycler_sprite)$/;
const isSpr = (p: string | undefined): p is string => !!p && /\.spr$/i.test(p.trim());

/** Pfad des Sprites, das die Entity zeigt, oder null.
 *  Reihenfolge: Feld model bei sprite()-Klassen und den eingebauten Sprite-Klassen, dann das Symbol iconsprite() der Klasse. */
export function spritePathOf(e: Pick<Entity, 'kv'>): string | null {
  const cls = e.kv.classname, def = EntDefs.get(cls);
  if (SPRITE_MODEL.test(cls) && e.kv.model) return e.kv.model;
  if (def.fgd) {
    if (def.sprite !== undefined) {
      if (def.sprite) return def.sprite;
      if (isSpr(e.kv.model)) return e.kv.model;
    } else if (def.studio !== undefined && isSpr(e.kv.model)) return e.kv.model;   // studio() mit Sprite im Feld model
    if (def.iconsprite) return def.iconsprite;
  }
  return null;
}

/** Pfad des Modells (.mdl), das die Entity zeigt, oder null: der feste Pfad aus studio("…") der Klasse, sonst das Feld model (bei studio() ohne Pfad) */
export function modelPathOf(e: Pick<Entity, 'kv'>): string | null {
  const def = EntDefs.get(e.kv.classname);
  if (!def.fgd || def.studio === undefined) return null;
  if (def.studio && /\.mdl$/i.test(def.studio)) return def.studio;
  return /\.mdl$/i.test(String(e.kv.model || '').trim()) ? String(e.kv.model).trim() : null;
}

/** Wie oft die Karte welches Modell braucht (Schlüssel über keyOf, z. B. der Dateiname) */
export function modelPaths(ents: Pick<Entity, 'kv'>[], keyOf: (v: string) => string): Map<string, number> {
  const m = new Map<string, number>();
  for (const e of ents) { const p = modelPathOf(e); if (p) { const k = keyOf(p); m.set(k, (m.get(k) || 0) + 1); } }
  return m;
}

/** Eigene Pfade eines Sprites als Schlüssel zählen: Anzahl der Entities je Sprite, das die Karte zeigen will */
export function spritePaths(ents: Pick<Entity, 'kv'>[], keyOf: (v: string) => string): Map<string, number> {
  const m = new Map<string, number>();
  for (const e of ents) { const p = spritePathOf(e); if (p) { const k = keyOf(p); m.set(k, (m.get(k) || 0) + 1); } }
  return m;
}

/** Box der Klasse in Weltkoordinaten relativ zum Ursprung der Entity; Größe je Achse und Mitte */
export function boxOf(cls: string): { min: number[]; max: number[]; size: number[]; center: number[] } | null {
  const b = EntDefs.get(cls).size;
  if (!b) return null;
  return { min: b[0], max: b[1], size: b[1].map((v, i) => v - b[0][i]), center: b[1].map((v, i) => (v + b[0][i]) / 2) };
}

/** Funktions-Brushes: Geometrie, die nur der Logik dient (Trigger, Leitern, Kaufzonen …) und im Spiel nicht oder nur als Volumen da ist.
 *  Sie hängen am Entities-Schalter und verschwinden mit den Punkt-Entities. */
const FUNCTION_CLASS = /^(trigger_|func_(ladder|buyzone|bomb_target|hostage_rescue|vip_safetyzone|escapezone|monsterclip|tankcontrols))/;
const FUNCTION_TEX = /^aaatrigger$/i;
export const isFunctionBrush = (cls: string, tex: string): boolean => FUNCTION_CLASS.test(cls) || FUNCTION_TEX.test(tex);

/** Würfel, den der Editor zeichnet, wenn die Klasse keine Box (size()) hat */
const FALLBACK_BOX = { min: [-8, -8, -8], max: [8, 8, 8] };

/** Lage eines Modells in der Box der Entity: waagerecht zentriert, Unterkante auf der Unterseite der Box.
 *  shift: Verschiebung der Modellpunkte (Mitte nach 0/0, Unterkante nach 0); at: Punkt relativ zum Ursprung der Entity, an dem das so
 *  ausgerichtete Modell steht. Gedreht wird um die senkrechte Achse durch die Mitte (angles), nicht um den Ursprung des Modells. */
export function modelAnchor(bounds: { min: number[]; max: number[] }, box: { min: number[]; max: number[] } | null): { shift: number[]; at: number[] } {
  const b = box || FALLBACK_BOX;
  return {
    shift: [-(bounds.min[0] + bounds.max[0]) / 2, -(bounds.min[1] + bounds.max[1]) / 2, -bounds.min[2]],
    at: [(b.min[0] + b.max[0]) / 2, (b.min[1] + b.max[1]) / 2, b.min[2]]
  };
}

/** Klassen, die das Spiel beim Start auf den Boden fallen lässt (DROP_TO_FLOOR): Waffen, Munition und Gegenstände.
 *  Mapper setzen sie meist 8 bis 40 Einheiten über den Boden. */
export const dropsToFloor = (cls: string | undefined): boolean => /^(weapon_|ammo_|item_)/.test(cls || '');
/** So weit sucht das Spiel nach dem Boden (Einheiten) */
export const DROP_MAX = 256;
